import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { GIButton, Modal } from 'app-components';

import { AV_COMMENT_COLS, AV_IMPROVEMENT_FEEDBACK_MAX_LENGTH } from '../../constants/ticketlist.constants';
import { IAutoVerificationModal } from '../../constants/ticketlist.interface';
import {
    StyledAvFormLabel,
    StyledAvFull,
    StyledAvModal,
    StyledAvPanels,
    StyledCharacterRemaining,
} from '../../constants/ticketlist.styled';
import { useAutoVerification } from '../../hooks/useAutoVerification';
import { useFormHandler } from '../../hooks/useFormHandler';
import { AvResultCard } from './AvResultCard';
import { ManualReviewPanel } from './ManualReviewPanel';

export const AutoVerificationModal = ({ onClose }: IAutoVerificationModal) => {
    const intl = useIntl();
    const { avManualReviewForm } = useFormHandler();
    const { autoVerification, submitManualReview } = useAutoVerification();
    const { handleSubmit, register, watch, formState } = avManualReviewForm;

    const improvementFeedback = watch('improvementFeedback') || '';

    const onSubmit = (formData: FieldValues) => submitManualReview(formData, onClose);

    return (
        <StyledAvModal>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal id="autoVerificationModal" classNames="auto-verification-modal" data-testid="auto-verification-modal">
                    <Modal.Header
                        id="autoVerificationModalHeader"
                        titleId="ticket.autoVerification.modal.markComplete.title"
                        data-testid="auto-verification-modal-header"
                        onHide={onClose}
                    />
                    <Modal.Body data-testid="auto-verification-modal-body">
                        <StyledAvPanels>
                            <AvResultCard data={autoVerification || {}} />
                            <ManualReviewPanel form={avManualReviewForm} avResult={autoVerification || {}} />
                        </StyledAvPanels>
                        <StyledAvFull>
                            <StyledAvFormLabel htmlFor="avImprovementFeedback" className="required-true">
                                {intl.formatMessage({
                                    id: 'ticket.autoVerification.manualReview.improvementFeedbackLabel',
                                })}
                            </StyledAvFormLabel>
                            <textarea
                                id="avImprovementFeedback"
                                aria-required
                                aria-describedby="av-improvement-feedback-count"
                                data-testid="av-improvement-feedback"
                                placeholder={intl.formatMessage({
                                    id: 'ticket.autoVerification.manualReview.improvementFeedbackPlaceholder',
                                })}
                                maxLength={AV_IMPROVEMENT_FEEDBACK_MAX_LENGTH}
                                cols={AV_COMMENT_COLS}
                                rows={2}
                                {...register('improvementFeedback')}
                            />
                            <StyledCharacterRemaining id="av-improvement-feedback-count" aria-live="polite">
                                {intl.formatMessage(
                                    { id: 'ticketList.characterLeft' },
                                    { charLeft: AV_IMPROVEMENT_FEEDBACK_MAX_LENGTH - improvementFeedback.length },
                                )}
                            </StyledCharacterRemaining>
                        </StyledAvFull>
                    </Modal.Body>
                    <Modal.Controls data-testid="auto-verification-modal-controls">
                        <GIButton
                            id="autoVerificationCancelButton"
                            onClickHandler={onClose}
                            variant="secondary"
                            labelId="ticket.autoVerification.button.cancel"
                        />
                        <GIButton
                            id="autoVerificationSubmitButton"
                            type="submit"
                            variant="primary"
                            isDisabled={!formState.isValid}
                            labelId="ticket.autoVerification.button.taskComplete"
                        />
                    </Modal.Controls>
                </Modal>
            </form>
        </StyledAvModal>
    );
};
